import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Check, Heart, DollarSign, Star, Users, ArrowRight, Mail } from 'lucide-react';
import Header from '../components/Header';
import SEOHead from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PartnersPage = () => {
  const [mode, setMode] = useState('referral'); // 'referral' | 'partner_signup'
  const [form, setForm] = useState({
    referrer_name: '',
    referrer_email: '',
    referrer_phone: '',
    friend_name: '',
    friend_email: '',
    friend_phone: '',
    wedding_date: '',
    business_name: '',
    vendor_type: '',
    notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    // Light client-side validation — matches backend rule
    if (!form.referrer_name.trim() || !form.referrer_email.trim()) {
      setError('Please share your name and email so we can reach you.');
      return;
    }
    if (mode === 'referral' && !form.friend_name && !form.friend_email && !form.friend_phone) {
      setError("Tell us at least one way to reach the couple you're referring (name, email, or phone).");
      return;
    }

    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/partners/refer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, kind: mode }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not submit. Please try again.');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white min-h-screen text-black" data-testid="partners-page">
      <SEOHead
        title="Partners Referral Program — Crystal Media"
        description="Refer a friend who's getting married, or become a Crystal Media referral partner. Get paid for connections — your friends and family get special pricing."
        url="/partners"
      />
      <Header variant="light" />

      {/* ─── Hero ─── */}
      <section className="pt-28 md:pt-36 pb-12 md:pb-20 px-6 text-center max-w-3xl mx-auto">
        <p className="text-[10px] tracking-[0.3em] uppercase text-black/40 mb-4">Crystal Media · Partners</p>
        <h1 className="font-presti text-4xl sm:text-5xl md:text-6xl tracking-[0.02em] leading-[1.05] mb-5 lowercase">
          partners referral program
        </h1>
        <p className="text-black/60 text-base md:text-lg leading-relaxed">
          There's someone getting married every day. You might as well be the one getting paid for it.
        </p>
      </section>

      {/* ─── Why ─── */}
      <section className="px-6 pb-16 md:pb-24 max-w-5xl mx-auto">
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          <div className="border border-black/10 p-7" data-testid="why-card-1">
            <Heart size={20} className="mb-4" />
            <h3 className="font-presti text-2xl mb-3">You&rsquo;re doing them a favour</h3>
            <p className="text-sm text-black/65 leading-relaxed">
              You&rsquo;re pointing your friends in the right direction. They&rsquo;ll love you for it. And chances are
              they may have already contacted us — but no one is collecting the commission on that referral.
              You might as well be the one to get paid for it.
            </p>
          </div>
          <div className="border border-black/10 p-7" data-testid="why-card-2">
            <Star size={20} className="mb-4" />
            <h3 className="font-presti text-2xl mb-3">We are the best</h3>
            <p className="text-sm text-black/65 leading-relaxed">
              We always deliver and we always out-do ourselves. Don&rsquo;t take our word for it — check our
              Google reviews. 14+ years in the industry, still growing. Your friends and family are safe with us.
            </p>
          </div>
          <div className="border border-black/10 p-7" data-testid="why-card-3">
            <DollarSign size={20} className="mb-4" />
            <h3 className="font-presti text-2xl mb-3">Special partner rates</h3>
            <p className="text-sm text-black/65 leading-relaxed">
              Become a partner and we&rsquo;ll onboard you with special pricing only your referrals get.
              They save. You make money. It&rsquo;s simple.
            </p>
          </div>
        </div>

        <div className="mt-10 md:mt-14 max-w-3xl mx-auto text-center px-2">
          <p className="font-presti text-2xl md:text-3xl leading-snug text-black/85 mb-3 lowercase">
            this works for vendors too.
          </p>
          <p className="text-black/55 text-sm md:text-base leading-relaxed">
            If you&rsquo;re a planner, florist, MUA, venue, DJ, or any wedding-industry vendor — let&rsquo;s talk.
            Long-term partnerships, real commissions, and clients you can confidently send our way.
          </p>
        </div>
      </section>

      {/* ─── Form ─── */}
      <section className="px-6 pb-24 max-w-2xl mx-auto" data-testid="partners-form-section">
        {submitted ? (
          <div className="border border-black/10 p-10 text-center" data-testid="partners-success">
            <Check size={24} className="mx-auto mb-4 text-black" />
            <h2 className="font-presti text-3xl mb-3">Thank you</h2>
            <p className="text-black/60 mb-6">
              We&rsquo;ll be in touch shortly — usually within a business day.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 border border-black/20 text-xs tracking-[0.2em] uppercase text-black/70 hover:border-black hover:text-black transition-colors"
            >
              Back home <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <>
            {/* Mode toggle */}
            <div className="flex border border-black/10 mb-8" data-testid="partners-mode-toggle">
              <button
                type="button"
                onClick={() => setMode('referral')}
                className={`flex-1 py-3 text-xs tracking-[0.2em] uppercase transition-colors ${
                  mode === 'referral' ? 'bg-black text-white' : 'text-black/60 hover:text-black'
                }`}
                data-testid="partners-mode-referral"
              >
                <Users size={14} className="inline mr-2 -mt-0.5" />
                Refer a friend
              </button>
              <button
                type="button"
                onClick={() => setMode('partner_signup')}
                className={`flex-1 py-3 text-xs tracking-[0.2em] uppercase transition-colors ${
                  mode === 'partner_signup' ? 'bg-black text-white' : 'text-black/60 hover:text-black'
                }`}
                data-testid="partners-mode-signup"
              >
                <DollarSign size={14} className="inline mr-2 -mt-0.5" />
                Become a partner
              </button>
            </div>

            <form onSubmit={submit} className="space-y-7" data-testid="partners-form">
              {/* About you */}
              <div className="space-y-5">
                <p className="text-[10px] tracking-[0.25em] uppercase text-black/40">About you</p>
                <Field label="Your name *" id="referrer_name" value={form.referrer_name} onChange={update('referrer_name')} required />
                <Field label="Your email *" id="referrer_email" type="email" autoComplete="email" inputMode="email" value={form.referrer_email} onChange={update('referrer_email')} required />
                <Field label="Your phone (optional)" id="referrer_phone" type="tel" autoComplete="tel" inputMode="tel" value={form.referrer_phone} onChange={update('referrer_phone')} />

                {mode === 'partner_signup' && (
                  <>
                    <Field label="Business name" id="business_name" value={form.business_name} onChange={update('business_name')} placeholder="If applicable" />
                    <Field label="Vendor type" id="vendor_type" value={form.vendor_type} onChange={update('vendor_type')} placeholder="e.g. Planner, Florist, MUA, Venue" />
                  </>
                )}
              </div>

              {/* About the friend */}
              {mode === 'referral' && (
                <div className="space-y-5">
                  <p className="text-[10px] tracking-[0.25em] uppercase text-black/40">About the couple</p>
                  <Field label="Their name" id="friend_name" value={form.friend_name} onChange={update('friend_name')} />
                  <Field label="Their email" id="friend_email" type="email" inputMode="email" value={form.friend_email} onChange={update('friend_email')} />
                  <Field label="Their phone" id="friend_phone" type="tel" inputMode="tel" value={form.friend_phone} onChange={update('friend_phone')} />
                  <Field label="Wedding date (if known)" id="wedding_date" value={form.wedding_date} onChange={update('wedding_date')} placeholder="e.g. June 2026" />
                  <p className="text-[11px] text-black/40 leading-relaxed">
                    At least one of name / email / phone is required so we can reach them.
                  </p>
                </div>
              )}

              {/* Notes */}
              <div>
                <label htmlFor="notes" className="block text-[10px] tracking-[0.2em] uppercase text-black/40 mb-2">
                  {mode === 'referral' ? 'Anything we should know?' : 'Tell us about your business / audience'}
                </label>
                <textarea
                  id="notes"
                  rows={4}
                  value={form.notes}
                  onChange={update('notes')}
                  data-testid="partners-notes"
                  className="w-full px-0 py-2.5 bg-transparent border-b border-black/20 focus:border-black outline-none resize-none transition-colors"
                  placeholder={mode === 'referral' ? 'How do you know them? Their vibe? Budget?' : 'Where do you operate? What kind of clients do you serve?'}
                />
              </div>

              {error && <p className="text-red-500 text-sm" data-testid="partners-error">{error}</p>}

              <button
                type="submit"
                disabled={busy}
                data-testid="partners-submit"
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-black text-white text-xs tracking-[0.2em] uppercase font-medium hover:bg-black/80 transition-colors disabled:bg-black/40 tap-target"
              >
                {busy
                  ? <><Loader2 size={14} className="animate-spin" /> Submitting…</>
                  : <><Mail size={14} /> {mode === 'referral' ? 'Send referral' : 'Apply as partner'}</>
                }
              </button>

              <p className="text-[11px] text-black/40 text-center leading-relaxed">
                No spam, ever. We&rsquo;ll reach out to confirm commission terms and (for referrals) the special pricing
                we&rsquo;ll extend to the couple.
              </p>
            </form>
          </>
        )}
      </section>

      <footer className="py-10 px-6 border-t border-black/5 text-center">
        <p className="text-[10px] tracking-[0.2em] uppercase text-black/40">
          <Link to="/" className="hover:text-black">Crystal Media</Link> · Toronto, Canada
        </p>
      </footer>
    </div>
  );
};

/* ─────────────── Field component ─────────────── */
const Field = ({ label, id, type = 'text', value, onChange, required, placeholder, autoComplete, inputMode }) => (
  <div>
    <label htmlFor={id} className="block text-[10px] tracking-[0.2em] uppercase text-black/40 mb-2">{label}</label>
    <input
      id={id}
      name={id}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      autoComplete={autoComplete}
      inputMode={inputMode}
      data-testid={`partners-field-${id}`}
      className="w-full px-0 py-2.5 bg-transparent border-b border-black/20 focus:border-black outline-none transition-colors"
    />
  </div>
);

export default PartnersPage;
